import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from 'src/app/services/user/user.service';
import { PlanService } from '../plan.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import * as alertifyjs from 'alertifyjs';
import { Router } from '@angular/router';

interface PlanMetrics {
  totalPlanes: number;
  planesActivos: number;
  planesCredito: number;
  clientesActivos: number;
  nuevosEstesMes: number;
  porcentajeActivos: number;
  promedioQuincenal: number;
  crecimientoMensual: number;
  proximosVencer: number;
}

interface PlanFilters {
  searchTerm: string;
  filterEstado: string;
  filterTipo: string;
  filterFecha: string;
  filterVisibilidad: string;
  filterPrioridad: string;
  filterProductType: string; // Nuevo filtro de tipo de producto
}

@Component({
  selector: 'app-plans-dashboard',
  templateUrl: './plans-dashboard.component.html',
  styleUrls: ['./plans-dashboard.component.css']
})
export class PlansDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data properties
  plans: any[] = [];
  filteredPlanes: any[] = [];
  paginatedPlanes: any[] = [];
  plansLoading = false;

  // Metrics
  metrics: PlanMetrics = {
    totalPlanes: 0,
    planesActivos: 0,
    planesCredito: 0,
    clientesActivos: 0,
    nuevosEstesMes: 0,
    porcentajeActivos: 0,
    promedioQuincenal: 0,
    crecimientoMensual: 0,
    proximosVencer: 0
  };

  // Filters and search
  filters: PlanFilters = {
    searchTerm: '',
    filterEstado: '',
    filterTipo: '',
    filterFecha: '',
    filterVisibilidad: '',
    filterPrioridad: '',
    filterProductType: 'CREDIT' // Por defecto CREDIT
  };

  // Filtro de tipo de producto
  productArray: any[] = []; // Lista de productos disponibles
  showProductDropdown: boolean = false; // Visibilidad del dropdown de productos
  isLoadingProducts: boolean = false; // Estado de carga de productos

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalPages = 0;
  startIndex = 0;
  endIndex = 0;
  visiblePages: number[] = [];

  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // View state
  viewMode: 'list' | 'cards' = 'list'; // Default to list as requested

  // Sistema de ayudas
  showHelp: { [key: string]: boolean } = {};
  helpData: any = {};

  constructor(
    private userService: UserService,
    private planService: PlanService,
    private offcanvasService: NgbOffcanvas,
    public authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadProductTypes(); // Cargar tipos de productos
    this.loadPlansData();
    // ELIMINADO: this.setupRealTimeUpdates(); - Timer innecesario que causaba llamadas cada 30s
    this.loadHelpData(); // Cargar ayudas
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load plans data and calculate metrics
   */
  async loadPlansData(showLoading = true): Promise<void> {
    try {
      this.plansLoading = showLoading;

      // Load plans from API with optimized parameters
      // ⚡ OPTIMIZACIÓN: Agregamos 'lean: true' para que el backend no popule campos innecesarios
      // Esto reduce significativamente el tamaño de la respuesta y mejora la velocidad
      // No enviamos el parámetro status para traer TODOS los planes
      const plansResponse = await this.userService.getplansall({ 
        lean: true,  // Solicitar solo campos esenciales sin populate
        product_type: this.filters.filterProductType // Filtro de tipo de producto
      });
      
      // ⚡ OPTIMIZACIÓN: Eliminada llamada a getplansInvoices() que relentizaba la carga
      // El conteo de créditos ahora se carga solo en la vista de detalle del plan

      // Process plans data
      this.plans = plansResponse?.res?.map((plan: any) => {
        return {
          ...plan,
          // Add version info if not present
          version: plan.version || { major: 1, minor: 0 },
          // Calculate estimated audience (mock calculation)
          estimatedAudience: this.calculateEstimatedAudience(plan)
        };
      }) || [];

      // Calculate metrics
      this.calculateMetrics();

      // Apply initial filters
      this.applyFilters();

    } catch (error) {
      console.error('Error loading plans:', error);
      alertifyjs.error('Error al cargar los planes');
    } finally {
      this.plansLoading = false;
    }
  }

  /**
   * Calculate dashboard metrics
   */
  private calculateMetrics(): void {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    this.metrics = {
      totalPlanes: this.plans.length,
      planesActivos: this.plans.filter(p => p.status === true).length,
      planesCredito: this.plans.filter(p => p.params?.category === 'CREDITO').length,
      clientesActivos: 0, // Ya no se calcula aquí para mejorar rendimiento
      nuevosEstesMes: this.plans.filter(p => new Date(p.date) >= thisMonth).length,
      porcentajeActivos: this.plans.length > 0 ? Math.round((this.plans.filter(p => p.status === true).length / this.plans.length) * 100) : 0,
      promedioQuincenal: this.calculateAverageQuincenal(),
      crecimientoMensual: 12, // Mock data - would be calculated from historical data
      proximosVencer: this.plans.filter(p => {
        if (!p.params?.end_date) return false;
        const endDate = new Date(p.params.end_date);
        return endDate >= now && endDate <= nextMonth;
      }).length
    };
  }

  /**
   * Calculate average quincenal amount
   */
  private calculateAverageQuincenal(): number {
    const quincenalPlans = this.plans.filter(p =>
      p.payment_terms?.some((term: any) => term.frequency?.type === 'quincenal')
    );

    if (quincenalPlans.length === 0) return 0;

    const totalAmount = quincenalPlans.reduce((sum, plan) => {
      const amount = plan.params?.max_amount_base || plan.params?.min_amount_base || 0;
      return sum + amount;
    }, 0);

    return Math.round(totalAmount / quincenalPlans.length);
  }

  /**
   * Calculate estimated audience for a plan
   */
  private calculateEstimatedAudience(plan: any): string {
    // Mock calculation based on filters and plan type
    let baseAudience = 1000;

    if (plan.visibility === 'PRIVATE') baseAudience *= 0.3;
    if (plan.params?.category === 'CREDITO') baseAudience *= 0.7;
    if (plan.params?.filters?.general?.invoices?.shop?.type === 'white_list') {
      baseAudience *= 0.5;
    }

    return `~${Math.round(baseAudience).toLocaleString()} clientes`;
  }

  /**
   * ELIMINADO: setupRealTimeUpdates()
   * Ya no necesitamos timer automático - solo cargar cuando se accede al módulo
   */

  /**
   * Search functionality
   */
  onSearch(): void {
    this.applyFilters();
  }

  /**
   * Filter change handler
   */
  onFilterChange(): void {
    this.applyFilters();
  }

  /**
   * Apply all filters and search
   */
  applyFilters(): void {
    let filtered = [...this.plans];

    // Search filter
    if (this.filters.searchTerm.trim()) {
      const searchTerm = this.filters.searchTerm.toLowerCase();
      filtered = filtered.filter(plan =>
        plan.code?.toLowerCase().includes(searchTerm) ||
        plan.name?.toLowerCase().includes(searchTerm) ||
        plan.description?.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (this.filters.filterEstado) {
      filtered = filtered.filter(plan => {
        if (this.filters.filterEstado === 'active') {
          return plan.status === true;
        } else if (this.filters.filterEstado === 'inactive') {
          return plan.status !== true;
        }
        return true;
      });
    }

    // Visibility filter
    if (this.filters.filterVisibilidad) {
      filtered = filtered.filter(plan =>
        plan.visibility === this.filters.filterVisibilidad
      );
    }

    // Priority filter
    if (this.filters.filterPrioridad) {
      filtered = filtered.filter(plan =>
        plan.params?.priority?.toString() === this.filters.filterPrioridad
      );
    }

    // Type filter (keeping for compatibility)
    if (this.filters.filterTipo) {
      filtered = filtered.filter(plan =>
        plan.params?.category === this.filters.filterTipo
      );
    }

    this.filteredPlanes = filtered;
    this.currentPage = 1;
    this.updatePagination();
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      filterEstado: '',
      filterTipo: '',
      filterFecha: '',
      filterVisibilidad: '',
      filterPrioridad: '',
      filterProductType: 'CREDIT' // Resetear a CREDIT por defecto
    };
    this.applyFilters();
    this.loadPlansData(); // Recargar con el filtro de producto por defecto
  }

  /**
   * Sort functionality
   */
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredPlanes.sort((a, b) => {
      let aValue = this.getFieldValue(a, field);
      let bValue = this.getFieldValue(b, field);

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    this.updatePagination();
  }

  /**
   * Get field value for sorting
   */
  private getFieldValue(obj: any, field: string): any {
    switch (field) {
      case 'code': return obj.code || '';
      case 'name': return obj.name || '';
      case 'type': return obj.params?.category || '';
      case 'status': return obj.status ? 'active' : 'inactive';
      case 'date': return new Date(obj.date || 0);
      case 'priority': return obj.params?.priority || 0;
      default: return '';
    }
  }

  /**
   * Pagination functionality
   */
  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredPlanes.length / this.pageSize);
    this.startIndex = (this.currentPage - 1) * this.pageSize;
    this.endIndex = Math.min(this.startIndex + this.pageSize, this.filteredPlanes.length);

    this.paginatedPlanes = this.filteredPlanes.slice(this.startIndex, this.endIndex);
    this.updateVisiblePages();
  }

  private updateVisiblePages(): void {
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    this.visiblePages = [];
    for (let i = start; i <= end; i++) {
      this.visiblePages.push(i);
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  goToPage(page: number): void {
    this.currentPage = page;
    this.updatePagination();
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  /**
   * Plan actions
   */
  verPlan(plan: any): void {
    // Navigate to detailed view (Module 2)
    this.router.navigate(['/console/plans/detail', plan.code]);
  }

  /**
   * Editar plan
   */
  editarPlan(plan: any): void {
    this.router.navigate(['/console/plans/edit', plan.code]);
  }

  /**
   * Borrar plan
   */
  async borrarPlan(plan: any): Promise<void> {
    try {
      debugger
      // Llamar al servicio para borrar el plan (usar _id como prioridad, luego code)
      const planId = plan._id;
      const response = await this.planService.deletePlan(planId);

      if (response && response.res === true) {
        alertifyjs.success(`Plan "${plan.name}" borrado exitosamente`);
        // Recargar la lista de planes
        await this.loadPlansData();
      } else {
        alertifyjs.error('Error al borrar el plan');
      }
    } catch (error: any) {
      console.error('Error al borrar plan:');

      // Manejar errores específicos del backend
      if (error?.code === 1 && error?.msg) {
        alertifyjs.error(error.msg); // "Créditos existentes con este Plan"
      } else if (error?.error?.msg) {
        alertifyjs.error(`Error al borrar el plan`);
      } else if (error?.message) {
        alertifyjs.error(`Error al borrar el plan`);
      } else {
        alertifyjs.error('Error al borrar el plan. Verifica tu conexión e intenta nuevamente.');
      }
    }
  }

  async duplicarPlan(plan: any): Promise<void> {
    try {
      // Create a copy of the plan with new code
      const duplicatedPlan = {
        ...plan,
        code: `${plan.code}_COPY_${Date.now()}`,
        name: `${plan.name} (Copia)`,
        date: new Date().toISOString(),
        version: { major: 1, minor: 0 }
      };

      delete duplicatedPlan._id;
      delete duplicatedPlan.estimatedAudience;

      await this.planService.createPlan(duplicatedPlan).toPromise();
      alertifyjs.success('Plan duplicado exitosamente');
      this.loadPlansData(false);

    } catch (error) {
      console.error('Error duplicating plan:', error);
      alertifyjs.error('Error al duplicar el plan');
    }
  }

  /**
   * Toggle plan status
   */
  async toggleEstado(plan: any): Promise<void> {
    try {
      const newStatus = !plan.status;
      const newState = newStatus ? 'ACTIVE' : 'INACTIVE';

      console.log(`🔄 Toggling plan status: ${plan.code} from ${plan.status} to ${newStatus}`);

      // Get the complete plan object first
      const planResponse = await this.planService.getPlanByCode(plan.code).toPromise();

      console.log('📡 Plan response from API:', planResponse);

      if (!planResponse || !planResponse.res) {
        throw new Error('No se pudo obtener el plan completo');
      }

      // Update only the status and state fields in the complete plan object
      const completePlan = { ...planResponse.res };
      completePlan.status = newStatus;
      completePlan.state = newState;

      console.log('📤 Sending updated plan to API:', {
        code: completePlan.code,
        status: completePlan.status,
        state: completePlan.state
      });

      // Send the complete plan object to the API
      const updateResponse = await this.planService.updatePlan(completePlan);

      console.log('📡 Update response:', updateResponse);

      const actionMessage = newStatus ? 'activado' : 'desactivado';
      alertifyjs.success(`Plan ${actionMessage} exitosamente`);

      // Update the plan status and state locally
      plan.status = newStatus;
      plan.state = newState;

      // Recalculate metrics with updated data
      this.calculateMetrics();
      this.applyFilters();

    } catch (error: any) {
      console.error('❌ Error toggling plan status:', error);

      // More detailed error logging
      if (error.error) {
        console.error('❌ API Error details:', error.error);
      }

      const errorMessage = error?.error?.msg ||
                          error?.error?.message ||
                          error?.message ||
                          'Error al cambiar estado del plan';

      alertifyjs.error(errorMessage);
    }
  }

  /**
   * Create new plan
   */
  crearNuevoPlan(): void {
    this.router.navigate(['/console/plans/new']);
  }

  /**
   * Track by function for ngFor performance
   */
  trackByPlanId(index: number, plan: any): any {
    return plan._id || plan.code || index;
  }

  /**
   * Get status badge classes
   */
  getStatusBadgeClass(status: boolean): string {
    return status
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  }

  /**
   * Get category badge classes
   */
  getCategoryBadgeClass(category: string): string {
    switch (category) {
      case 'CONTADO': return 'bg-green-100 text-green-800';
      case 'CREDITO': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: string | Date): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Get version display string
   */
  getVersionString(version: any): string {
    if (!version) return 'v1.0';
    return `v${version.major || 1}.${version.minor || 0}`;
  }

  /**
   * Get payment terms text for display
   */
  getPaymentTermsText(paymentTerms: any[]): string {
    if (!paymentTerms || paymentTerms.length === 0) return 'No definido';

    const terms = paymentTerms.map(term => {
      if (term.frequency?.type === 'quincenal') {
        return `${term.frequency.count || 4} cuotas quincenales`;
      } else if (term.frequency?.type === 'semanal') {
        return `${term.frequency.count || 4} cuotas semanales`;
      } else if (term.frequency?.type === 'personalizado') {
        return `${term.frequency.count || 2} cuotas personalizadas`;
      }
      return term.name || 'Término personalizado';
    });

    return terms.join(', ');
  }

  /**
   * Get quotas text for table display
   */
  getQuotasText(plan: any): string {
    if (!plan.payment_terms || plan.payment_terms.length === 0) return '1';

    const firstTerm = plan.payment_terms[0];
    if (firstTerm.installments) {
      return firstTerm.installments.toString();
    }

    return '1'; // Default
  }

  /**
   * Get frequency text for table display
   */
  getFrequencyText(plan: any): string {
    if (!plan.payment_terms || plan.payment_terms.length === 0) return 'Quincenal';

    const firstTerm = plan.payment_terms[0];
    if (firstTerm.frequency?.type === 'quincenal') {
      return 'Quincenal';
    } else if (firstTerm.frequency?.type === 'semanal') {
      return 'Semanal';
    } else if (firstTerm.frequency?.type === 'personalizado') {
      return 'Personalizado';
    }

    return firstTerm.name || 'Quincenal';
  }

  /**
   * Get priority text for table display
   */
  getPriorityText(plan: any): string {
    const priority = plan.params?.priority || 0;
    return priority.toString();
  }

  /**
   * Get created by display name with capitalization
   * Optimizado para manejar casos donde user no está poblado
   */
  getCreatedByDisplayName(plan: any): string {
    const user = plan.created_by?.user;
    
    // Si user es un ObjectId string en lugar de un objeto poblado
    if (typeof user === 'string') return '-';
    
    if (!user || (!user.name && !user.lastName)) return '-';

    const name = this.capitalizeFirstLetter(user.name || '');
    const lastName = this.capitalizeFirstLetter(user.lastName || '');

    return `${name} ${lastName}`.trim() || '-';
  }

  /**
   * Get updated by display name with capitalization
   * Optimizado para manejar casos donde user no está poblado
   */
  getUpdatedByDisplayName(plan: any): string {
    const user = plan.updated_by?.user;
    
    // Si user es un ObjectId string en lugar de un objeto poblado
    if (typeof user === 'string') return '-';
    
    if (!user || (!user.name && !user.lastName)) return '-';

    const name = this.capitalizeFirstLetter(user.name || '');
    const lastName = this.capitalizeFirstLetter(user.lastName || '');

    return `${name} ${lastName}`.trim() || '-';
  }

  /**
   * Capitalize first letter of a string
   */
  private capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  // Sistema de ayudas
  toggleHelp(section: string): void {
    this.showHelp[section] = !this.showHelp[section];
  }

  async loadHelpData(): Promise<void> {
    this.helpData = {
      'dashboard': {
        title: '¿Cómo usar el Dashboard de Planes?',
        icon: 'fas fa-info-circle',
        color: 'blue',
        description: 'Panel principal para gestionar todos los planes de crédito de KREDIT.',
        sections: [
          {
            title: 'Métricas Principales',
            items: [
              { label: 'Total de Planes', description: 'Cantidad total de planes creados en el sistema' },
              { label: 'Planes Activos', description: 'Planes disponibles para los clientes' },
              { label: 'Planes Inactivos', description: 'Planes deshabilitados temporalmente' },
              { label: 'Próximos a Vencer', description: 'Planes que se desactivarán pronto' }
            ]
          },
          {
            title: 'Vistas Disponibles',
            items: [
              { label: 'Vista Lista', description: 'Tabla detallada con toda la información', icon: 'fas fa-list' },
              { label: 'Vista Tarjetas', description: 'Cards visuales con información resumida', icon: 'fas fa-th' }
            ]
          }
        ],
        tip: { icon: '💡', title: 'Tip', text: 'Usa los filtros para encontrar planes específicos rápidamente' }
      },
      'filtros': {
        title: '¿Cómo funcionan los Filtros?',
        icon: 'fas fa-info-circle',
        color: 'green',
        description: 'Herramientas para encontrar planes específicos según diferentes criterios.',
        sections: [
          {
            title: 'Tipos de Filtro',
            items: [
              { label: 'Búsqueda', description: 'Busca por nombre, código o descripción del plan' },
              { label: 'Estado', description: 'Filtra por planes activos o inactivos' },
              { label: 'Visibilidad', description: 'Planes públicos (todos) o privados (específicos)' },
              { label: 'Prioridad', description: 'Nivel de importancia del plan (0-3)' }
            ]
          }
        ],
        tip: { icon: '🔍', title: 'Búsqueda', text: 'Combina múltiples filtros para resultados más precisos' }
      },
      'acciones': {
        title: '¿Qué acciones puedo realizar?',
        icon: 'fas fa-info-circle',
        color: 'purple',
        description: 'Operaciones disponibles para gestionar los planes.',
        sections: [
          {
            title: 'Acciones Principales',
            items: [
              { label: 'Ver', description: 'Consultar detalles completos del plan', icon: 'fas fa-eye' },
              { label: 'Duplicar', description: 'Crear una copia del plan para modificar', icon: 'fas fa-copy' },
              { label: 'Activar/Desactivar', description: 'Cambiar disponibilidad del plan', icon: 'fas fa-toggle-on' }
            ]
          }
        ],
        tip: { icon: '⚡', title: 'Eficiencia', text: 'Duplica planes similares para ahorrar tiempo en configuración' }
      }
    };
  }

  // ========== MÉTODOS PARA FILTRO DE TIPO DE PRODUCTO ==========
  
  /**
   * Cargar lista de tipos de productos
   */
  async loadProductTypes(): Promise<void> {
    try {
      this.isLoadingProducts = true;
      const response = await this.userService.getTypesProductsInvoices();
      
      // El servicio devuelve un array de objetos con params.code
      const products = response?.resp || [];
      
      // La respuesta ya viene con la estructura correcta { params: { code, name } }
      this.productArray = products;
      
      // Asegurar que CREDIT esté en la lista
      if (!this.productArray.find((p: any) => p?.params?.code === 'CREDIT')) {
        this.productArray.unshift({
          params: {
            code: 'CREDIT',
            name: 'CREDIT'
          }
        });
      }
      
      console.log('✅ Productos cargados:', this.productArray.length);
      console.log('📦 Productos:', this.productArray.map((p: any) => p?.params?.code));
    } catch (error) {
      console.error('Error loading product types:', error);
      // Si falla, al menos tener CREDIT como opción por defecto
      this.productArray = [{
        params: {
          code: 'CREDIT',
          name: 'CREDIT'
        }
      }];
    } finally {
      this.isLoadingProducts = false;
    }
  }

  /**
   * Toggle dropdown de productos
   */
  toggleProductDropdown(): void {
    this.showProductDropdown = !this.showProductDropdown;
  }

  /**
   * Cerrar dropdown de productos
   */
  closeProductDropdown(): void {
    this.showProductDropdown = false;
  }

  /**
   * Seleccionar tipo de producto
   */
  selectProductType(productCode: string): void {
    this.filters.filterProductType = productCode;
    this.showProductDropdown = false;
    this.loadPlansData(); // Recargar planes con el nuevo filtro
  }

  /**
   * Obtener el texto del producto seleccionado
   */
  getSelectedProductText(): string {
    const selected = this.productArray.find((p: any) => p?.params?.code === this.filters.filterProductType);
    return selected?.params?.code || 'CREDIT';
  }

}
